import { getUserRecommendations } from '@atlaskit/smart-user-picker';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type GroupBase,
} from '@databases/atlaskit/Select';
import { getCloudId } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { type CreateMentionNotificationProps } from '@databases/mention-notification/types';
import { type KnownUser, type UserReference } from '@databases/types/confluence';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { AvatarWithLabel, UserCard } from '@databases/user';
import {
  type MultiChange,
  MultiValueChangeType,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { generateId } from '@databases/utils/utils';
import React, { type ReactNode, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { getIdentity } from '@canvas/connection/identity';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling,
} from '../popover-edit-common';

import { type ResolvedUser, type Value } from './types';

const getOptionValue = (value: KnownUser | UserReference) => {
  return value.accountId;
};

export const isFullUser = (value: KnownUser | UserReference): value is KnownUser => {
  return 'type' in value;
};

interface UserSelectProps {
  resolvedUsers: ResolvedUser[];
  onChange: (value: Value[], change: ValueChange | null) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  contentId: string;
  field: DatabaseField<DatabaseFieldType.USER>;
  autoFocus?: boolean;
  isPending?: boolean;
  isRenderedInSidePanel?: boolean;
  isMulti: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  scheduleMentionNotification: (params: CreateMentionNotificationProps) => void;
  initialKey?: string;
}

const getUserRecommendationsParams = async (query: string) => {
  return {
    context: {
      contextType: 'contributors',
      principalId: (await getIdentity())?.accountId || '',
      productAttributes: {
        isEntitledConfluenceExternalCollaborator: false,
      },
      productKey: 'confluence',
      siteId: getCloudId() || '',
    },
    includeGroups: false,
    includeTeams: false,
    includeUsers: true,
    maxNumberOfResults: 15,
    performSearchQueryOnly: false,
    query,
  };
};

/**
 * An asynchronous select box that allows searching for users within the Confluence instance.
 */
const UserSelect: React.FunctionComponent<UserSelectProps> = ({
  resolvedUsers,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isPending,
  contentId,
  field,
  isRenderedInSidePanel,
  isMulti,
  addDataSectionRef,
  scheduleMentionNotification,
  initialKey,
}) => {
  const [inputValue, setInputValue] = useState(initialKey ?? '');
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);
  const intl = useIntl();
  const formatMessage = intl.formatMessage;

  const loadOptions = useCallback(
    async (query: string): Promise<(KnownUser | UserReference)[]> => {
      const data = await getUserRecommendations(
        await getUserRecommendationsParams(query),
        intl
      );

      return data.map((user) => ({
        accountId: user.id,
        displayName: user.name,
        type: 'known',
        profilePicture: {
          path: user.avatarUrl,
        },
      }));
    },
    [intl]
  );

  const keepUnselectedUsers = useCallback(
    (
      options: (KnownUser | UserReference | GroupBase<KnownUser | UserReference>)[]
    ): (KnownUser | UserReference | GroupBase<KnownUser | UserReference>)[] =>
      options.filter((option) => {
        if (!('accountId' in option)) {
          return true;
        }

        const isAlreadySelected = resolvedUsers.some(
          (u) => 'accountId' in u && u.accountId === option.accountId
        );

        return !isAlreadySelected;
      }),
    [resolvedUsers]
  );

  const onItemAdd = useCallback(
    async (val: Value) => {
      const userValues = [...resolvedUsers, val];

      const delta: MultiChange<DatabaseFieldType.USER> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val],
          };

      onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta,
      });

      const user = val as UserReference;
      if (
        field.settings.mentionNotificationEnabled &&
        contentId &&
        user.displayName &&
        !isRenderedInSidePanel
      ) {
        scheduleMentionNotification({
          mentions: [
            {
              mentionLocalId: val._id,
              mentionedUserAccountId: user.accountId,
              displayName: user.displayName,
            },
          ],
          fieldId: field.id,
        });
      }
    },
    [
      resolvedUsers,
      isMulti,
      onChange,
      field.settings.mentionNotificationEnabled,
      field.id,
      contentId,
      scheduleMentionNotification,
      isRenderedInSidePanel,
    ]
  );

  const handleChange = useCallback(
    (
      value:
        | KnownUser
        | {
            accountId: string;
            displayName?: string | undefined;
          }
        | null
    ) => {
      if (!value) {
        return;
      }
      const valToAdd = {
        _id: generateId(),
        accountId: 'accountId' in value ? value.accountId : '',
        displayName: 'displayName' in value ? value.displayName : '',
      };
      onItemAdd(valToAdd);
    },
    [onItemAdd]
  );

  const formatOptionLabel = useCallback(
    (
      value: KnownUser | UserReference,
      meta: FormatOptionLabelMeta<KnownUser | UserReference>
    ): ReactNode => {
      if (meta.context === 'menu') {
        if (isFullUser(value)) {
          return <AvatarWithLabel userOrGroup={value} label={value.displayName} />;
        } else {
          return (
            <UserCard
              accountId={value.accountId}
              displayName={value.displayName}
              variant="avatar"
            />
          );
        }
      }
    },
    []
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInSidePanel]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isMulti) {
      e.preventDefault();
      addDataSectionRef.current?.getElementsByTagName('input')[0].blur();
    }
  };

  return (
    // FIXME: Accessibility issue - jsx-a11y/no-static-element-interactions
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div style={{ width: '100%' }} onKeyDown={handleKeyDown}>
      <AsyncSelect<KnownUser | UserReference>
        classNamePrefix="user-picker"
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        loadOptions={loadOptions}
        getOptionValue={getOptionValue}
        modifyOptions={keepUnselectedUsers}
        onFocus={onFocus}
        onBlur={handleBlur}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        autoFocus={autoFocus}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        placeholder={formatMessage(messages['user-picker-popover.search-text'])}
        loadingMessage={() =>
          formatMessage(messages['field-settings.select-picker-loading-text'])
        }
        isLoading={isPending}
        styles={
          isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
        }
        controlShouldRenderValue={false}
        {...(!isRenderedInSidePanel && {
          menuPortalTarget: null,
          defaultMenuIsOpen: true,
          menuIsOpen: true,
          components: {
            IndicatorsContainer: () => null,
            ValueContainer: CustomValueContainer,
          },
        })}
      />
    </div>
  );
};

export default UserSelect;
